import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Users, MapPin, Award, Clock, Shield, Droplet } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEOHead from '../components/SEOHead';
import RecentDonorsFeed from '../components/RecentDonorsFeed';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

/* --------------------------------------------------------------------------
   Animated counter — counts from 0 to target when element enters viewport
-------------------------------------------------------------------------- */
const AnimatedCounter = ({ value, suffix = '', prefix = '' }) => {
  const [displayed, setDisplayed] = useState(0);
  const ref = useRef(null);
  const animated = useRef(false);

  useEffect(() => {
    if (!value || animated.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !animated.current) {
          animated.current = true;
          const duration = 1500;
          const steps = 50;
          const increment = value / steps;
          let current = 0;
          const timer = setInterval(() => {
            current += increment;
            if (current >= value) {
              setDisplayed(value);
              clearInterval(timer);
            } else {
              setDisplayed(Math.floor(current));
            }
          }, duration / steps);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value]);

  return (
    <span ref={ref}>
      {prefix}{typeof displayed === 'number' ? displayed.toLocaleString() : displayed}{suffix}
    </span>
  );
};

/* --------------------------------------------------------------------------
   Skeleton stat card for loading state
-------------------------------------------------------------------------- */
const StatSkeleton = () => (
  <Card className="text-center border-none shadow-md">
    <CardContent className="pt-6 pb-6">
      <div className="skeleton h-10 w-24 mx-auto mb-2 rounded-lg" />
      <div className="skeleton h-4 w-32 mx-auto rounded" />
    </CardContent>
  </Card>
);

const Home = () => {
  const [globalStats, setGlobalStats] = useState(null); // null = loading
  const [urgentRequests, setUrgentRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    fetchUrgentRequests();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats/global`);
      setGlobalStats(response.data);
    } catch (error) {
      setGlobalStats({ registeredDonors: 0, activeRequests: 0, activeCities: 0, livesSaved: 0 });
    } finally {
      setLoading(false);
    }
  };

  const fetchUrgentRequests = async () => {
    try {
      const response = await axios.get(`${API}/blood-requests?urgency=critical&status=active&limit=3`);
      setUrgentRequests(response.data || []);
    } catch (error) {
      // handled silently
    }
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'critical': return 'bg-red-700 text-white';
      case 'high': return 'bg-orange-600 text-white';
      case 'medium': return 'bg-yellow-600 text-white';
      default: return 'bg-gray-600 text-white';
    }
  };

  // Schema.org structured data
  const homeSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "DonorsHouse",
    "alternateName": "DonorsHouse - Connecting Donors Worldwide",
    "url": process.env.REACT_APP_SITE_URL || "https://donorshouse.com",
    "description": "Connect blood and organ donors with those in need across 195 countries. Register as a donor, find donation centers, and save lives worldwide.",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${process.env.REACT_APP_SITE_URL || "https://donorshouse.com"}/blood-requests?search={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    },
    "publisher": {
      "@type": "NGO",
      "name": "DonorsHouse",
      "parentOrganization": { "@type": "NGO", "name": "New Abilities Foundation" },
      "logo": {
        "@type": "ImageObject",
        "url": `${process.env.REACT_APP_SITE_URL || "https://donorshouse.com"}/donorshouse-transparent-1000.png`
      }
    }
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "NGO",
    "name": "DonorsHouse",
    "url": process.env.REACT_APP_SITE_URL || "https://donorshouse.com",
    "logo": `${process.env.REACT_APP_SITE_URL || "https://donorshouse.com"}/donorshouse-transparent-1000.png`,
    "description": "DonorsHouse is a division of New Abilities Foundation, a 501(c)(3) nonprofit organization. We connect blood and organ donors worldwide.",
    "parentOrganization": {
      "@type": "NGO",
      "name": "New Abilities Foundation",
      "url": "https://newabilities.org",
      "nonprofitStatus": "NonprofitType"
    },
    "nonprofitStatus": "NonprofitType",
    "foundingDate": "2025",
    "areaServed": { "@type": "Place", "name": "Worldwide" },
    "knowsAbout": ["Blood Donation", "Organ Donation", "Medical Donations", "Healthcare", "Life-Saving Procedures"]
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How does DonorsHouse connect donors with recipients?",
        "acceptedAnswer": { "@type": "Answer", "text": "DonorsHouse provides a platform where donors can register their blood type or organ donation preferences, and recipients can post urgent requests. Our system matches donors with requests based on location, blood type, and urgency." }
      },
      {
        "@type": "Question",
        "name": "How many countries does DonorsHouse serve?",
        "acceptedAnswer": { "@type": "Answer", "text": "DonorsHouse serves 195+ countries worldwide, providing donation regulations information for 212 countries and connecting donors and recipients globally." }
      },
      {
        "@type": "Question",
        "name": "Is it safe to donate through DonorsHouse?",
        "acceptedAnswer": { "@type": "Answer", "text": "Yes, all donations are conducted by certified medical professionals following strict safety protocols. DonorsHouse connects you with verified donation centers and hospitals." }
      }
    ]
  };

  const breadcrumbs = [{ name: 'Home', url: process.env.REACT_APP_SITE_URL || 'https://donorshouse.com' }];

  return (
    <>
      <SEOHead
        title="DonorsHouse - Connect Blood & Organ Donors Worldwide | Save Lives"
        description="Join DonorsHouse to connect blood and organ donors with recipients in need. Register as a donor, find donation centers, and access global donation regulations. Save lives across 195+ countries."
        keywords="blood donation, organ donation, donor registry, blood bank, organ transplant, medical donation, save lives, global donors, donation centers"
        schema={[homeSchema, organizationSchema, faqSchema]}
        breadcrumbs={breadcrumbs}
      />
      <div className="bg-white">

        {/* ============================================================
            HERO SECTION — emotional illustration + gradient overlay
        ============================================================ */}
        <section className="relative overflow-hidden" aria-label="Hero — Give the Gift of Life">
          {/* Background: hero illustration with gradient overlay */}
          <div className="absolute inset-0 z-0">
            <img
              src="/hero-donors.jpg"
              alt="Diverse group of people connected by the gift of life through donation"
              className="w-full h-full object-cover object-center"
              width="1920"
              height="700"
              fetchpriority="high"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-red-950/90 via-red-900/80 to-red-800/60" aria-hidden="true" />
          </div>

          {/* Decorative animated wave at bottom */}
          <div className="absolute bottom-0 left-0 right-0 z-10" aria-hidden="true">
            <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
              <path d="M0 60L48 50C96 40 192 20 288 15C384 10 480 20 576 27.5C672 35 768 40 864 37.5C960 35 1056 25 1152 20C1248 15 1344 15 1392 15L1440 15V60H1392C1344 60 1248 60 1152 60C1056 60 960 60 864 60C768 60 672 60 576 60C480 60 384 60 288 60C192 60 96 60 48 60H0Z" fill="white"/>
            </svg>
          </div>

          {/* Hero content */}
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 lg:py-40">
            <div className="text-center max-w-3xl mx-auto animate-fade-in-up">
              {/* Pre-headline badge */}
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-6">
                <Heart className="h-4 w-4 text-red-300 animate-heartbeat" aria-hidden="true" fill="currentColor" />
                <span className="text-red-100 text-sm font-medium tracking-wide uppercase">195+ Countries · Save Lives</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-7xl font-extrabold text-white mb-6 leading-tight tracking-tight">
                Give the{' '}
                <span className="text-red-300">Gift</span>{' '}
                of Life
              </h1>
              <p className="text-xl sm:text-2xl mb-10 text-red-100 max-w-2xl mx-auto leading-relaxed">
                Connect blood and organ donors with those in need across the globe.
                Every donation can save up to <strong className="text-white">8 lives</strong>.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/register">
                  <Button
                    size="lg"
                    className="w-full sm:w-auto bg-white text-red-950 hover:bg-red-50 font-bold text-lg px-10 py-6 btn-transition shadow-xl animate-pulse-glow"
                    aria-label="Register to become a blood or organ donor"
                  >
                    Become a Donor
                    <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
                  </Button>
                </Link>
                <Link to="/blood-requests">
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full sm:w-auto border-2 border-white text-white hover:bg-white hover:text-red-950 font-semibold text-lg px-10 py-6 btn-transition backdrop-blur-sm bg-white/5"
                    aria-label="Find urgent blood requests near you"
                  >
                    Find Blood Now
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ============================================================
            GLOBAL IMPACT STATS
        ============================================================ */}
        <section className="py-16 bg-white" aria-label="Global impact statistics" data-testid="global-stats">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="font-bold text-gray-700 uppercase tracking-widest text-sm mb-2">Our Global Impact</h2>
            </div>
            {/* aria-live so screen readers announce updated numbers */}
            <div
              className="grid grid-cols-2 lg:grid-cols-4 gap-6"
              aria-live="polite"
              aria-atomic="false"
            >
              {loading ? (
                <>
                  <StatSkeleton /><StatSkeleton /><StatSkeleton /><StatSkeleton />
                </>
              ) : (
                <>
                  <Card className="text-center border-none shadow-lg card-hover animate-fade-in-up delay-100">
                    <CardContent className="pt-8 pb-6">
                      <div className="w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <Users className="h-7 w-7 text-red-700" aria-hidden="true" />
                      </div>
                      <div className="text-3xl lg:text-4xl font-extrabold text-red-700 mb-1">
                        <AnimatedCounter value={globalStats.registeredDonors} suffix="+" />
                      </div>
                      <p className="text-gray-600 font-medium text-sm">Registered Donors</p>
                    </CardContent>
                  </Card>
                  <Card className="text-center border-none shadow-lg card-hover animate-fade-in-up delay-200">
                    <CardContent className="pt-8 pb-6">
                      <div className="w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <Droplet className="h-7 w-7 text-red-700" aria-hidden="true" />
                      </div>
                      <div className="text-3xl lg:text-4xl font-extrabold text-red-700 mb-1">
                        <AnimatedCounter value={globalStats.activeRequests} />
                      </div>
                      <p className="text-gray-600 font-medium text-sm">Active Requests</p>
                    </CardContent>
                  </Card>
                  <Card className="text-center border-none shadow-lg card-hover animate-fade-in-up delay-300">
                    <CardContent className="pt-8 pb-6">
                      <div className="w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <MapPin className="h-7 w-7 text-red-700" aria-hidden="true" />
                      </div>
                      <div className="text-3xl lg:text-4xl font-extrabold text-red-700 mb-1">
                        <AnimatedCounter value={globalStats.activeCities} />
                      </div>
                      <p className="text-gray-600 font-medium text-sm">Active Cities</p>
                    </CardContent>
                  </Card>
                  <Card className="text-center border-none shadow-lg card-hover animate-fade-in-up delay-400">
                    <CardContent className="pt-8 pb-6">
                      <div className="w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <Heart className="h-7 w-7 text-red-700" aria-hidden="true" fill="currentColor" />
                      </div>
                      <div className="text-3xl lg:text-4xl font-extrabold text-red-700 mb-1">
                        <AnimatedCounter value={globalStats.livesSaved} suffix="+" />
                      </div>
                      <p className="text-gray-600 font-medium text-sm">Lives Saved</p>
                    </CardContent>
                  </Card>
                </>
              )}
            </div>
          </div>
        </section>

        {/* ============================================================
            HOW IT WORKS
        ============================================================ */}
        <section className="py-20 lg:py-28 bg-gray-50" aria-label="How DonorsHouse works">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-extrabold text-gray-900 mb-4">
                How DonorsHouse Works
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Connecting donors and recipients seamlessly across the globe — in 3 simple steps
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  step: '01',
                  icon: Users,
                  title: 'Register as Donor',
                  description: 'Sign up in minutes and register your blood type or pledge organs. Join our global community of life-savers.',
                  delay: 'delay-100',
                },
                {
                  step: '02',
                  icon: MapPin,
                  title: 'Find Help Nearby',
                  description: 'Search for blood requests, donation centers, and available donors in your area or anywhere in the world.',
                  delay: 'delay-200',
                },
                {
                  step: '03',
                  icon: Heart,
                  title: 'Save Lives',
                  description: 'Your donation can save up to 8 lives. Make a difference today and become part of something bigger.',
                  delay: 'delay-300',
                  iconFill: true,
                },
              ].map(({ step, icon: Icon, title, description, delay, iconFill }) => (
                <Card
                  key={step}
                  className={`border-none shadow-lg card-hover bg-white animate-fade-in-up ${delay} group`}
                >
                  <CardHeader className="pb-0">
                    <span className="step-number mb-3" aria-hidden="true">{step}</span>
                    <div className="w-14 h-14 bg-gradient-to-br from-red-100 to-red-50 rounded-2xl flex items-center justify-center mb-4 group-hover:from-red-200 group-hover:to-red-100 transition-colors">
                      <Icon className="h-7 w-7 text-red-700" aria-hidden="true" {...(iconFill ? { fill: 'currentColor' } : {})} />
                    </div>
                    <CardTitle className="text-xl font-bold text-gray-900">{title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 text-base leading-relaxed">
                      {description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* ============================================================
            URGENT REQUESTS (shown when data exists)
        ============================================================ */}
        {urgentRequests.length > 0 && (
          <section className="py-16 bg-red-50" aria-label="Urgent blood requests">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
                <div>
                  <div className="inline-flex items-center gap-2 mb-2">
                    <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse" aria-hidden="true" />
                    <span className="text-red-600 text-sm font-bold uppercase tracking-wide">Live</span>
                  </div>
                  <h2 className="text-3xl font-extrabold text-gray-900">Urgent Blood Requests</h2>
                  <p className="text-gray-600 mt-1">People need your help right now</p>
                </div>
                <Link to="/blood-requests">
                  <Button className="bg-red-700 hover:bg-red-800 text-white btn-transition" aria-label="View all blood requests">
                    View All Requests
                    <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                  </Button>
                </Link>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {urgentRequests.map((request) => (
                  <Card key={request.id} className="border-red-200 shadow-md card-hover bg-white">
                    <CardHeader>
                      <div className="flex items-center justify-between mb-2">
                        <Badge className={getUrgencyColor(request.urgency)}>
                          {request.urgency?.toUpperCase()}
                        </Badge>
                        <Badge variant="outline" className="font-bold text-red-900 border-red-300 text-lg">
                          {request.blood_type}
                        </Badge>
                      </div>
                      <CardTitle className="text-lg text-gray-900">{request.hospital}</CardTitle>
                      <CardDescription className="text-gray-600">
                        {request.city}, {request.country}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm text-gray-700">
                        <p><strong>Needed:</strong> {request.units_needed} units</p>
                        <p><strong>Reason:</strong> {request.reason || 'Not specified'}</p>
                        <div className="flex items-center text-gray-500 mt-3">
                          <Clock className="h-4 w-4 mr-1" aria-hidden="true" />
                          <span className="text-xs">
                            {request.created_at
                              ? `Posted ${Math.floor((new Date() - new Date(request.created_at)) / (1000 * 60 * 60 * 24))} days ago`
                              : 'Recently posted'}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ============================================================
            RECENT DONORS FEED
        ============================================================ */}
        <section className="py-16 bg-white" aria-label="Recent donors who joined our community">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <RecentDonorsFeed />
          </div>
        </section>

        {/* ============================================================
            WHY DONATE — with animated impact counters
        ============================================================ */}
        <section className="py-20 lg:py-28 bg-gray-50" aria-label="Why your donation matters">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl lg:text-4xl font-extrabold text-gray-900 mb-8">
                  Why Your Donation Matters
                </h2>
                <div className="space-y-7">
                  {[
                    {
                      icon: Heart,
                      iconFill: true,
                      title: 'Save Multiple Lives',
                      body: 'One organ donor can save up to 8 lives and enhance 75+ others through tissue donation.',
                    },
                    {
                      icon: Shield,
                      title: 'Safe & Secure',
                      body: 'All donations are conducted by certified medical professionals following strict safety protocols.',
                    },
                    {
                      icon: Award,
                      title: 'Global Impact',
                      body: 'Join donors from 195 countries making a difference in their communities and worldwide.',
                    },
                  ].map(({ icon: Icon, iconFill, title, body }) => (
                    <div key={title} className="flex items-start group">
                      <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-2xl flex items-center justify-center group-hover:bg-red-200 transition-colors">
                        <Icon className="h-6 w-6 text-red-700" aria-hidden="true" {...(iconFill ? { fill: 'currentColor' } : {})} />
                      </div>
                      <div className="ml-5">
                        <h3 className="text-lg font-bold text-gray-900 mb-1">{title}</h3>
                        <p className="text-gray-600 leading-relaxed">{body}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Animated Impact Numbers */}
              <div className="bg-gradient-to-br from-red-900 to-red-700 rounded-3xl p-10 shadow-2xl text-white">
                <h3 className="text-xl font-bold text-red-200 uppercase tracking-wide mb-8 text-center">
                  The Numbers Don't Lie
                </h3>
                <div className="grid grid-cols-2 gap-8">
                  {[
                    { value: 17, suffix: '', label: 'People die daily\nwaiting for organs' },
                    { value: 100000, suffix: '+', label: 'On transplant\nwaiting lists' },
                    { value: 8, suffix: '', label: 'Lives saved\nby one donor' },
                    { value: 75, suffix: '+', label: 'Lives enhanced\nby tissue donation' },
                  ].map(({ value, suffix, label }) => (
                    <div key={label} className="text-center">
                      <div className="text-4xl lg:text-5xl font-extrabold text-white mb-2">
                        <AnimatedCounter value={value} suffix={suffix} />
                      </div>
                      <p className="text-red-200 text-sm font-medium leading-snug whitespace-pre-line">{label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============================================================
            CTA SECTION
        ============================================================ */}
        <section className="py-20 lg:py-24 relative overflow-hidden" aria-label="Call to action — Register as organ donor">
          <div className="absolute inset-0 hero-gradient" aria-hidden="true" />
          <div
            className="absolute inset-0 opacity-5"
            aria-hidden="true"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
            }}
          />
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="animate-float mb-6" aria-hidden="true">
              <Heart className="h-16 w-16 text-red-300 mx-auto" fill="currentColor" />
            </div>
            <h2 className="text-3xl lg:text-5xl font-extrabold text-white mb-4 leading-tight">
              Ready to Make a Difference?
            </h2>
            <p className="text-xl text-red-100 mb-10 max-w-2xl mx-auto">
              Your decision today could give someone a second chance at life tomorrow.
              Join thousands of donors worldwide.
            </p>
            <Link to="/register?type=organ">
              <Button
                size="lg"
                className="bg-white text-red-950 hover:bg-red-50 font-bold text-lg px-12 py-7 btn-transition shadow-2xl"
                aria-label="Register as an organ donor on DonorsHouse"
              >
                Register as an Organ Donor
                <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
              </Button>
            </Link>
          </div>
        </section>

      </div>
    </>
  );
};

export default Home;